import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PlusIcon, PencilIcon, TrashIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { fetchHeroContent, createHeroContent, updateHeroContent, deleteHeroContent } from '../../store/slices/heroContentSlice';
import { showSuccess, showError } from '../../store/slices/notificationSlice';
import { Table, Button, Badge, Pagination, ConfirmDialog, Card } from '../../components/Common';
import HeroContentModal from '../../components/Modals/HeroContentModal';

const HeroContentList = () => {
  const dispatch = useDispatch();
  const { heroContent, loading, pagination } = useSelector((state) => state.heroContent);
  
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });

  useEffect(() => {
    dispatch(fetchHeroContent({ page, size: pageSize }));
  }, [dispatch, page, pageSize]);

  const openModal = (item = null) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingItem(null);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingItem) {
        await dispatch(updateHeroContent({ id: editingItem.id, data: formData })).unwrap();
        dispatch(showSuccess('Hero content updated successfully'));
      } else {
        await dispatch(createHeroContent(formData)).unwrap();
        dispatch(showSuccess('Hero content created successfully'));
      }
      closeModal();
      dispatch(fetchHeroContent({ page, size: pageSize }));
    } catch (error) {
      console.error('Submit error:', error);
      dispatch(showError(error || 'Failed to save hero content'));
      throw error; // Re-throw to let modal handle it
    }
  };

  const handleDelete = async () => {
    if (deleteModal.id) {
      try {
        await dispatch(deleteHeroContent(deleteModal.id)).unwrap();
        dispatch(showSuccess('Hero content deleted successfully'));
        dispatch(fetchHeroContent({ page, size: pageSize }));
      } catch (error) {
        dispatch(showError(error || 'Failed to delete hero content'));
      }
      setDeleteModal({ open: false, id: null });
    }
  };

  const columns = [
    {
      key: 'title',
      title: 'Hero Content',
      render: (value, row) => (
        <div className="flex items-center gap-3">
          {row.image_url || row.coverImageUrl ? (
            <img src={row.image_url || row.coverImageUrl} alt="" className="w-16 h-10 rounded-lg object-cover" />
          ) : (
            <div className="w-16 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <PhotoIcon className="w-6 h-6 text-purple-600" />
            </div>
          )}
          <div>
            <p className="font-medium text-slate-800 line-clamp-1">{value || 'Untitled'}</p>
            <p className="text-sm text-slate-500 line-clamp-1">{row.subtitle || row.heading || 'No subtitle'}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'link_url',
      title: 'Link',
      render: (value, row) => (
        <span className="text-slate-600 text-sm truncate max-w-xs block">{value || row.link || '-'}</span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      render: (value) => (
        <Badge variant={value?.name === 'Active' || value?.id === 1 ? 'success' : 'warning'} dot>
          {value?.name || 'Active'}
        </Badge>
      ),
    },
    {
      key: 'created_at',
      title: 'Created',
      render: (value) => value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_, row) => (
        <div className="flex items-center gap-2">
          <button onClick={() => openModal(row)} className="p-2 hover:bg-slate-100 rounded-lg" title="Edit">
            <PencilIcon className="w-4 h-4 text-slate-500" />
          </button>
          <button onClick={() => setDeleteModal({ open: true, id: row.id })} className="p-2 hover:bg-red-50 rounded-lg" title="Delete">
            <TrashIcon className="w-4 h-4 text-red-500" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Hero Content</h1>
          <p className="text-slate-500">Manage homepage hero sections</p>
        </div>
        <Button icon={PlusIcon} onClick={() => openModal()}>New Hero Content</Button>
      </div>

      <Card noPadding>
        <Table columns={columns} data={heroContent} loading={loading} emptyMessage="No hero content found" emptyIcon={PhotoIcon} />

        {pagination && pagination.totalPages > 1 && (
          <Pagination currentPage={page} totalPages={pagination.totalPages} totalItems={pagination.total} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
        )}
      </Card>

      {/* Hero Content Modal */}
      <HeroContentModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSubmit={handleSubmit}
        heroContent={editingItem}
      />

      <ConfirmDialog isOpen={deleteModal.open} onClose={() => setDeleteModal({ open: false, id: null })} onConfirm={handleDelete} title="Delete Hero Content" message="Are you sure you want to delete this hero content?" type="danger" confirmText="Delete" />
    </div>
  );
};

export default HeroContentList;
